
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import AppLayout from '@/components/app/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { motion } from 'framer-motion';
import { Sparkles, Calendar, CreditCard, User } from 'lucide-react';
import JUVCreditCounter from '@/components/dashboard/JUVCreditCounter';
import QuickActions from '@/components/dashboard/QuickActions';
import TreatmentProgress from '@/components/dashboard/TreatmentProgress';
import GlowJourney from '@/components/dashboard/GlowJourney';

const AppDashboard = () => {
  const { user } = useAuth();

  // Fetch credit balance
  const { data: creditBalance, isLoading: creditLoading } = useQuery({
    queryKey: ['creditBalance', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      const { data, error } = await supabase.rpc('get_credit_balance', {
        user_uuid: user.id
      });
      
      if (error) {
        console.error('Error fetching credit balance:', error);
        return 0;
      }
      
      return data || 0;
    },
    enabled: !!user?.id
  });

  // Fetch recent appointments
  const { data: appointments } = useQuery({
    queryKey: ['recentAppointments', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from('appointments')
        .select('*')
        .eq('user_id', user.id)
        .order('appointment_date', { ascending: false })
        .limit(3);
      
      if (error) {
        console.error('Error fetching appointments:', error);
        return [];
      }
      
      return data || [];
    },
    enabled: !!user?.id
  });

  // Fetch user profile
  const { data: profile } = useQuery({
    queryKey: ['profile', user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      
      return data;
    },
    enabled: !!user?.id
  });

  const firstName = profile?.first_name || 'Welcome';

  // Calculate treatment progress data
  const completedTreatments = appointments?.filter(apt => apt.status === 'completed')?.length || 0;
  const treatmentGoal = 4; // Monthly goal
  const treatmentPercentage = completedTreatments / treatmentGoal * 100;

  // Calculate glow journey data
  const glowScore = Math.min(85 + completedTreatments * 5, 100); // Base score + treatment bonus
  const streak = 7; // This would be calculated from actual treatment dates
  const nextAppointment = appointments?.find(apt => apt.status === 'scheduled' && new Date(apt.appointment_date) > new Date());

  return (
    <AppLayout>
      <div className="min-h-screen bg-gradient-to-br from-juv-cream-white to-juv-soft-blush">
        <div className="container mx-auto px-4 py-8">
          {/* Welcome Header */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            className="mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-8 h-8 text-juv-terracotta" />
              <h1 className="text-3xl font-bold text-juv-deep-chestnut">
                Welcome back, {firstName}!
              </h1>
            </div>
            <p className="text-gray-600">
              Ready to continue your JUV journey? Here's your personalized dashboard.
            </p>
          </motion.div>

          {/* Main Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Credits & Quick Actions */}
            <div className="lg:col-span-2 space-y-6">
              {/* Credit Balance */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <JUVCreditCounter balance={creditBalance || 0} />
              </motion.div>

              {/* Quick Actions */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <QuickActions />
              </motion.div>

              {/* Recent Appointments */}
              <motion.div 
                initial={{ opacity: 0, x: -20 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <Card className="bg-slate-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
                      <Calendar className="w-5 h-5" />
                      Recent Appointments
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {appointments && appointments.length > 0 ? (
                      <div className="space-y-3">
                        {appointments.map((appointment, index) => (
                          <div key={index} className="flex justify-between items-center p-3 bg-juv-soft-blush rounded-lg">
                            <div>
                              <p className="font-medium text-juv-deep-chestnut">
                                {appointment.treatment_type || 'Treatment'}
                              </p>
                              <p className="text-sm text-gray-600">
                                {appointment.appointment_date ? new Date(appointment.appointment_date).toLocaleDateString() : 'Date TBD'}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs ${
                              appointment.status === 'completed' 
                                ? 'bg-green-100 text-green-800' 
                                : appointment.status === 'scheduled' 
                                  ? 'bg-blue-100 text-blue-800' 
                                  : 'bg-gray-100 text-gray-800'
                            }`}>
                              {appointment.status || 'Scheduled'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500">
                        <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
                        <p>No appointments yet</p>
                        <p className="text-sm">Book your first JUV treatment!</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Right Column - Progress & Journey */}
            <div className="space-y-6">
              {/* Treatment Progress */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <TreatmentProgress 
                  completed={completedTreatments} 
                  goal={treatmentGoal} 
                  percentage={treatmentPercentage} 
                />
              </motion.div>

              {/* JUV Journey */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <GlowJourney 
                  glowScore={glowScore} 
                  streak={streak} 
                  nextAppointment={nextAppointment} 
                />
              </motion.div>

              {/* Membership Status */}
              <motion.div 
                initial={{ opacity: 0, x: 20 }} 
                animate={{ opacity: 1, x: 0 }} 
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <Card className="bg-slate-50">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-juv-deep-chestnut">
                      <User className="w-5 h-5" />
                      JUV Membership
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-4">
                      <div className="w-16 h-16 bg-gradient-to-r from-juv-terracotta to-juv-deep-chestnut rounded-full flex items-center justify-center mx-auto mb-4">
                        <Sparkles className="w-8 h-8 text-white" />
                      </div>
                      <h3 className="font-semibold text-juv-deep-chestnut mb-2">
                        {profile?.membership_tier || 'Standard'} Member
                      </h3>
                      <p className="text-sm text-gray-600 mb-4">
                        Status: {profile?.membership_status === 'active' ? 'Active' : 'Inactive'}
                      </p>
                      {profile?.membership_status !== 'active' && (
                        <p className="text-xs text-juv-terracotta">
                          Upgrade to unlock exclusive benefits!
                        </p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default AppDashboard;
